import { useEffect, useState } from "react";

import PresentedTable from "./PresentedTable";
import ErrorState from "./states/ErrorState";
import Skeleton from "./states/Skeleton";
import { obfuscatePII, useDemoMode } from "../lib/demoMode";
import { getSupabase } from "../lib/supabase";
import { track } from "../lib/telemetry";
import { useTenantId } from "../lib/tenant";
import { scopeTenantTable } from "../lib/tenantScope.mjs";
import {
  applyFeedQuality,
  applyFilters,
  applyNowCutoff,
  applyTimeline,
  drillReadPlan,
  resolveLatestFilters,
  resolveSourceName,
} from "../lib/queries";
import { DRILL_ROW_CAP } from "../lib/drillQuery.mjs";
import { isColumnMissingError } from "../lib/sanityGate";
import { presentTable } from "../lib/tablePresentation";
import type { ManifestWidget } from "../lib/manifest";

/**
 * Click-through drill-down modal (MER-115 Phase 5 acceptance).
 *
 * When a user clicks a chart or KPI tile, this modal opens and runs a
 * row-level query against the widget's underlying source: the widget's own
 * columns, for the rows that match the widget's own filters and window. The
 * intent is "show me the rows that aggregate to this value" without the user
 * needing to write SQL.
 *
 * MER-222 closed the gap between that intent and the query. Three changes,
 * each of which was a way the drill could disagree with its own tile:
 *
 *   1. The projection is the widget's columns, not `*`. Internal plumbing is
 *      no longer sent and then dropped on the client; it is not requested.
 *      A projection names columns, never rows, so this cannot widen tenant
 *      scope, and scopeTenantTable still binds tenant_id on top of RLS.
 *   2. `resolveLatestFilters` runs first. Six widgets per tenant pin
 *      `{op: latest}` (Safety and Fleet), and applyFilters throws on an
 *      unresolved clause by design, so every one of those drills opened on a
 *      PostgREST sentence instead of rows.
 *   3. `applyFeedQuality` runs last, with the shape the KPI executor would
 *      have used. A tile that excluded FEED_INCOMPLETE periods now drills
 *      into the same exclusion, so the rows can add up to the number.
 *
 * If the projection names a column this database does not have yet (the repo
 * one migration ahead of prod), the read is retried wide once rather than
 * failing: the reader still gets their rows, and the presentation layer drops
 * the plumbing as it always did.
 */

/** What a PostgREST read hands back once the builder is awaited. */
interface DrillRead {
  data: Record<string, unknown>[] | null;
  error: { message: string } | null;
}

export interface DrillDownModalProps {
  widget: ManifestWidget;
  open: boolean;
  onClose: () => void;
}

export default function DrillDownModal(props: DrillDownModalProps) {
  const { widget, open, onClose } = props;
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);
  const tenantId = useTenantId();
  const demoMode = useDemoMode();

  // MER-208: one widget_drill per modal open.
  useEffect(() => {
    if (!open) return;
    track("widget_drill", { widget_id: widget.id, widget_name: widget.name });
  }, [open, widget.id, widget.name]);

  useEffect(() => {
    if (!open || !widget.source) return;
    // Fail-closed: don't fetch underlying rows until the slug resolves
    // to a tenant_id, or the drill-down would leak cross-tenant rows.
    if (!tenantId) {
      setLoading(true);
      return;
    }
    const boundTenantId = tenantId;
    let cancelled = false;

    async function run() {
      if (!widget.source) return;
      setLoading(true);
      setError(null);
      setRows(null);
      try {
        const supabase = getSupabase();
        const table = resolveSourceName(widget.source);
        // MER-589: a `{op: latest}` pin is not a PostgREST predicate. It has to
        // be resolved to a literal before the query builds, exactly as
        // executeKpiQuery does, or applyFilters throws.
        const bound = await resolveLatestFilters(widget, boundTenantId);
        if (cancelled) return;
        const plan = drillReadPlan(bound, table);
        // MER-438 addendum: inherit the parent widget's filter + timeline so the
        // drill-down shows the SAME slice the tile aggregated. Without this, the
        // "Roll Off P&L" drill-down rendered Front Load / Side Load / Unknown
        // rows (ignored equipment_type) and mixed monthly + weekly grains
        // (ignored period_type). applyFilters pins both.
        const read = async (select: string): Promise<DrillRead> => {
          let q = scopeTenantTable(supabase.from(table).select(select), boundTenantId, table);
          q = applyFilters(q, bound);
          q = applyTimeline(q, bound);
          q = applyNowCutoff(q, bound, table);
          q = applyFeedQuality(q, table, plan.feedShape);
          return (await q.limit(DRILL_ROW_CAP)) as DrillRead;
        };
        let result = await read(plan.select);
        if (cancelled) return;
        // The projection is built from the manifest, which can be one migration
        // ahead of this database. A named-column miss is a deploy-order fact,
        // not a reason to show the reader an error over rows that exist.
        if (result.error && plan.select !== "*" && isColumnMissingError(result.error.message)) {
          result = await read("*");
          if (cancelled) return;
        }
        if (result.error) throw new Error(result.error.message);
        setRows(result.data ?? []);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [open, widget, tenantId]);

  if (!open) return null;

  // MER-438: the drill-down used to dump raw SELECT * rows (tenant_id, machine
  // columns, raw enums) at the customer. Route it through the presentation layer
  // so internal plumbing is dropped and headers/cells are humanized + formatted.
  // useAllowList: false + no cap -- the drill-down shows underlying DETAIL, not
  // the summary table's curated subset, but HIDDEN_COLUMNS still never render.
  const presented =
    rows && rows.length > 0
      ? presentTable(widget, rows, {
          obfuscate: (value, column) => obfuscatePII(value, column, demoMode),
          useAllowList: false,
          maxColumns: Number.POSITIVE_INFINITY,
        })
      : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Drill-down: ${widget.name}`}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10, 15, 13, 0.45)",
        display: "grid",
        placeItems: "center",
        padding: 32,
        zIndex: 100,
      }}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <article
        style={{
          background: "var(--bg-card)",
          borderRadius: 14,
          padding: "20px 24px",
          width: "min(1100px, 100%)",
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow: "var(--shadow-card)",
          display: "grid",
          gridTemplateRows: "auto 1fr",
          gap: 12,
        }}
      >
        <header style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center" }}>
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                color: "var(--text-muted)",
              }}
            >
              Drill-down
            </div>
            <h2 style={{ fontSize: 18, marginTop: 4 }}>{widget.name}</h2>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
              Source: {widget.source?.name ?? "(none)"} | up to {DRILL_ROW_CAP} rows
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              font: "inherit",
              padding: "8px 12px",
              border: "1px solid var(--border-subtle)",
              borderRadius: 8,
              background: "transparent",
              color: "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </header>
        <div>
          {isLoading ? (
            <Skeleton label="Loading underlying rows" height={120} />
          ) : error ? (
            <ErrorState message={error} />
          ) : presented && presented.rows.length > 0 ? (
            <PresentedTable
              columns={presented.columns}
              rows={presented.rows}
              hiddenByCap={presented.hiddenByCap}
            />
          ) : (
            <ErrorState message="No rows returned for this widget's source." title="Empty source" />
          )}
        </div>
      </article>
    </div>
  );
}
